import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Calendar } from '@/frontend/components/ui/calendar'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/frontend/components/ui/input-group'
import { Popover, PopoverContent, PopoverTrigger } from '@/frontend/components/ui/popover'
import { cn } from '@/frontend/lib/utils'

export interface DateInputProps {
  id?: string
  /** Wall-clock date as YYYY-MM-DD. */
  value?: string
  onChange?: (value: string | undefined) => void
  onBlur?: () => void
  disabled?: boolean
  className?: string
  /** Disable all dates before this date (YYYY-MM-DD). */
  fromDate?: string
  'aria-invalid'?: boolean
}

// ─── Parsing & formatting ─────────────────────────────────────────────────────

/** Build and validate a Date, returning undefined on overflow (e.g. Feb 31). */
function makeDate(day: number, month: number, year: number): Date | undefined {
  if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1000 || year > 9999) return undefined
  const d = new Date(year, month - 1, day)
  if (d.getDate() !== day || d.getMonth() !== month - 1) return undefined
  return d
}

/**
 * Parse a date string in one of two supported formats:
 *   dd/mm/yyyy  — primary UK input format
 *   yyyy-mm-dd  — ISO / paste from spreadsheets or databases
 *
 * Returns undefined if the string doesn't match or represents an invalid date.
 */
function parseDate(text: string): Date | undefined {
  const s = text.trim()

  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (dmy) return makeDate(parseInt(dmy[1], 10), parseInt(dmy[2], 10), parseInt(dmy[3], 10))

  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (iso) return makeDate(parseInt(iso[3], 10), parseInt(iso[2], 10), parseInt(iso[1], 10))

  return undefined
}

/** Format a Date as dd/mm/yyyy for the text input. */
export function toDisplayDDMMYYYY(date: Date): string {
  return [
    String(date.getDate()).padStart(2, '0'),
    String(date.getMonth() + 1).padStart(2, '0'),
    date.getFullYear(),
  ].join('/')
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Free-text date input with an inline calendar picker button.
 * Accepts dd/mm/yyyy or yyyy-mm-dd. Valid input is normalised to dd/mm/yyyy on blur.
 * Invalid inputs are rejected — the last valid value is restored on blur.
 * Built on InputGroup so the border wraps both the text input and the calendar button.
 *
 * Public API is YYYY-MM-DD strings throughout; JS `Date` only exists
 * transiently inside the component (for the Calendar primitive and dd/mm/yyyy
 * formatting). This keeps callers TZ-safe — no UTC-vs-local round-trip bug at
 * the props boundary.
 */
export function DateInput({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  className,
  fromDate,
  'aria-invalid': ariaInvalid,
}: DateInputProps) {
  // Parse string props into local-midnight Dates for the Calendar primitive.
  const valueDate = useMemo(() => (value ? parseDate(value) : undefined), [value])
  const fromDateAsDate = useMemo(() => (fromDate ? parseDate(fromDate) : undefined), [fromDate])

  const [inputText, setInputText] = useState(() => (valueDate ? toDisplayDDMMYYYY(valueDate) : ''))
  const [calendarMonth, setCalendarMonth] = useState<Date | undefined>(valueDate)
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Track the last value we emitted so we can distinguish external changes
  // (form reset, programmatic update) from our own onChange calls.
  const lastValueRef = useRef(value)

  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value
      setInputText(valueDate ? toDisplayDDMMYYYY(valueDate) : '')
    }
    // Always keep the calendar navigated to the value's month, even if the
    // change came from our own onChange (e.g. user typed a date in the input).
    if (valueDate) setCalendarMonth(valueDate)
  }, [value, valueDate])

  function emit(date: Date | undefined) {
    const next = date ? format(date, 'yyyy-MM-dd') : undefined
    lastValueRef.current = next
    onChange?.(next)
  }

  function handleBlur() {
    const date = parseDate(inputText)
    if (date) {
      // Valid — normalise the display and emit.
      setInputText(toDisplayDDMMYYYY(date))
      emit(date)
    } else if (inputText.trim().length === 0) {
      // Intentionally cleared — emit undefined.
      emit(undefined)
    } else {
      // Unparseable — snap back to the last valid value (or empty).
      // Don't call onChange; the form value is unchanged.
      setInputText(valueDate ? toDisplayDDMMYYYY(valueDate) : '')
    }
    onBlur?.()
  }

  function handleCalendarSelect(date: Date | undefined) {
    const text = date ? toDisplayDDMMYYYY(date) : ''
    setInputText(text)
    emit(date)
    onBlur?.()
    setOpen(false)
    inputRef.current?.focus()
  }

  return (
    <InputGroup className={cn('w-full', className)}>
      <InputGroupInput
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        onBlur={handleBlur}
        placeholder="dd/mm/yyyy"
        disabled={disabled}
        aria-invalid={ariaInvalid}
      />
      <InputGroupAddon align="inline-end">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={<InputGroupButton variant="ghost" size="icon-xs" disabled={disabled} />}
          >
            <CalendarIcon className="size-4" />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={valueDate}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              onSelect={handleCalendarSelect}
              disabled={fromDateAsDate ? { before: fromDateAsDate } : undefined}
              autoFocus
            />
          </PopoverContent>
        </Popover>
      </InputGroupAddon>
    </InputGroup>
  )
}
